import TaskQuickActions from "@/components/TaskQuickActions";
import { userError } from "@/lib/userError";
import React, { useEffect, useRef, useState } from "react";
import { CheckSquare, Target, ExternalLink, User, Plus } from "lucide-react";
import { getTasks, getTaskCounts, createTask, updateTask, getGrowth } from "@/lib/apiClient";
import { useData } from "@/lib/useData";
import { ContentSearch } from "@/components/ContentSearch";
import { matchesContent } from "@/lib/contentSearch";
import { PageHeader, LoadingCards, ErrorState, EmptyState, RefreshNotice } from "@/components/Common";
import { PRIORITY, TASK_STATUS } from "@/lib/constants";
import {taskDate as fiDate,taskTime as fiTime,taskDay,taskDeadline} from "@/lib/taskDates";
import {useLocation} from "react-router-dom";
import {EmailTaskSuggestions} from "@/components/EmailTaskSuggestions";
import {EmailTaskCase} from "@/components/EmailTaskCase";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { safeExternalUrl } from "@/lib/safeUrl";
import { requestKeyFor, resetRequestKey } from "@/lib/idempotency";
import { DateShortcuts } from "@/components/DateShortcuts";
import { EditItemDialog } from "@/components/EditItemDialog";
import {getTaskItem} from "@/lib/apiClient";
import {useLinkedRecord} from "@/lib/useLinkedRecord";
import {LinkedRecord} from "@/components/LinkedRecord";

const FILTERS = [
  { id: "all", label: "Kaikki" },
  { id: "todo", label: "Tekemättä" },
  { id: "doing", label: "Työn alla" },
  { id: "done", label: "Valmis" },
];

const EMPTY_COPY = {
  all: ["Ei tehtäviä", "Luo tehtävä tai pyydä assistenttia lisäämään se."],
  todo: ["Ei tekemättömiä tehtäviä", "Kaikki aloittamattomat tehtävät on hoidettu."],
  doing: ["Ei keskeneräisiä tehtäviä", "Kun aloitat tehtävän, merkitse sen tilaksi Kesken."],
  done: ["Ei valmiita tehtäviä", "Valmiiksi merkityt tehtävät näkyvät täällä."],
};

const localDay = taskDay;

const dueState = (task) => {
  if (!task.due || task.status === "done") return null;
  const dueDay = localDay(task.due);
  if (dueDay === null) return null;
  const today = localDay(new Date());
  if (dueDay < today) return "overdue";
  if (task.due.includes("T") && new Date(task.due).getTime() < Date.now()) return "overdue";
  if (dueDay === today) return "today";
  return "future";
};

const taskOrder = (left, right) => {
  const rank = { overdue: 0, today: 1, future: 2 };
  if (left.status === "done" && right.status !== "done") return 1;
  if (right.status === "done" && left.status !== "done") return -1;
  const stateDiff = (rank[dueState(left)] ?? 3) - (rank[dueState(right)] ?? 3);
  if (stateDiff) return stateDiff;
  const dueDiff = (new Date(left.due || 8640000000000000).getTime()) -
    (new Date(right.due || 8640000000000000).getTime());
  if (Number.isFinite(dueDiff) && dueDiff) return dueDiff;
  const priorities = { high: 0, medium: 1, normal: 1, low: 2 };
  return (priorities[left.priority] ?? 3) - (priorities[right.priority] ?? 3);
};

const NewTaskDialog = ({ onCreated, testid = "new-task-btn", triggerLabel = "Uusi tehtävä" }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("medium");
  const [due, setDue] = useState("");
  const [visibility, setVisibility] = useState("personal");
  const [saving, setSaving] = useState(false);
  const formId = React.useId();
  const [formError, setFormError] = useState("");
  const [invalidField, setInvalidField] = useState("");
  const requestRef = useRef({ key: "", fingerprint: "" });

  const submit = async () => {
    if (!title.trim()) { setInvalidField(formId + "-field0"); setFormError("Anna tehtävälle nimi."); document.getElementById(formId + "-field0")?.focus(); return; }
    setSaving(true);
    try {
      const payload = { title, priority, due: due || null, visibility };
      await createTask({ ...payload, idempotency_key: requestKeyFor(requestRef, payload) });
      toast.success("Tehtävä luotu.");
      resetRequestKey(requestRef);
      setTitle(""); setPriority("medium"); setDue(""); setVisibility("personal"); setOpen(false); await onCreated();
    } catch (requestError) {
      toast.error(userError(requestError, "Tehtävän luominen epäonnistui."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" data-testid={testid} className="glow-action inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold"><Plus className="h-4 w-4" /> {triggerLabel}</button>
      </DialogTrigger>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl max-w-sm mx-auto">
        <DialogHeader><DialogTitle>Uusi tehtävä</DialogTitle></DialogHeader>
        <form id={formId} onSubmit={(event) => { event.preventDefault(); setFormError(""); submit(); }} onChange={() => { setFormError(""); setInvalidField(""); }} onInvalidCapture={(event) => { event.preventDefault(); if (event.target === event.currentTarget.querySelector(":invalid")) { setInvalidField(event.target.id); setFormError("Tarkista kenttä: " + (event.target.labels?.[0]?.textContent || "pakollinen tieto")); event.target.focus(); } }} className="space-y-3">
          <div><Label htmlFor={formId + "-field0"} className="text-xs">Tehtävän nimi</Label><Input id={formId + "-field0"} aria-invalid={invalidField === formId + "-field0"} aria-describedby={formId + "-error"} required value={title} onChange={(event) => setTitle(event.target.value)} className="mt-1 rounded-xl" /></div>
          <div><Label htmlFor={formId + "-field1"} className="text-xs">Eräpäivä</Label><Input id={formId + "-field1"} aria-invalid={invalidField === formId + "-field1"} aria-describedby={formId + "-error"} type="date" value={due} onChange={(event) => setDue(event.target.value)} className="mt-1 rounded-xl" /><DateShortcuts value={due} onChange={setDue} /></div>
          <div><Label htmlFor={formId + "-field2"} className="text-xs">Prioriteetti</Label><Select value={priority} onValueChange={setPriority}><SelectTrigger id={formId + "-field2"} className="mt-1 rounded-xl"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="high">Korkea</SelectItem><SelectItem value="medium">Keskitaso</SelectItem><SelectItem value="low">Matala</SelectItem></SelectContent></Select></div>
          <div><Label htmlFor={formId + "-field3"} className="text-xs">Näkyvyys</Label><Select value={visibility} onValueChange={setVisibility}><SelectTrigger id={formId + "-field3"} className="mt-1 rounded-xl"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="personal">Vain minulle</SelectItem><SelectItem value="company">Yrityksen yhteinen</SelectItem></SelectContent></Select></div>
        </form>
        <p id={formId + "-error"} role="alert" className="text-sm text-destructive">{formError}</p>
        <DialogFooter><button type="submit" form={formId} disabled={saving} className="glow-action w-full rounded-full py-2.5 text-sm font-semibold disabled:opacity-50">{saving ? "Tallennetaan…" : "Luo tehtävä"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Tasks() {
  const location=useLocation();
  const suggestionId=new URLSearchParams(location.search).get("suggestion");
  const selection=useLinkedRecord("task",getTaskItem,"tasks");
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const { data, loading, error, refetch, refreshing, updatedAt } = useData(() => getTasks(filter), [filter], ["tasks"]);
  const objectives = useData(getGrowth);
  const counts=useData(getTaskCounts,[],["tasks"]);
  const [older,setOlder]=useState([]);const [loadingOlder,setLoadingOlder]=useState(false);const [hasMore,setHasMore]=useState(true);
  const generation=useRef(0);const pageScope=useRef(null);
  useEffect(()=>{generation.current+=1;pageScope.current=null;setOlder([]);setLoadingOlder(false);setHasMore(true);},[data,filter]);
  const [updatingTask, setUpdatingTask] = useState("");
  const emptyCopy = EMPTY_COPY[filter] || EMPTY_COPY.all;
  const allLoaded=[...new Map([...(data||[]),...(pageScope.current?.data===data&&pageScope.current?.filter===filter?older:[])].map(task=>[task.id,task])).values()];
  const visibleTasks = [...allLoaded].sort(taskOrder).filter((task) => task.id!==selection.data?.id && matchesContent(query, [task.title, task.assignee]));
  const refreshTasks=async()=>Promise.all([refetch(),counts.refetch(),selection.present ? selection.refetch() : Promise.resolve()]);

  const changeStatus = async (id, status) => {
    const previousStatus = (data || []).find((task) => task.id === id)?.status || (selection.data?.id===id ? selection.data.status : undefined);
    setUpdatingTask(id);
    try {
      await updateTask(id, { status });
      await refreshTasks();
      toast.success("Tehtävän tila päivitetty.", previousStatus && previousStatus !== status ? {
        action: {
          label: "Kumoa",
          onClick: async () => {
            try {
              await updateTask(id, { status: previousStatus });
              await refreshTasks();
              toast.success("Muutos kumottu.");
            } catch {
              toast.error("Muutoksen kumoaminen epäonnistui.");
            }
          },
        },
      } : undefined);
    } catch (requestError) {
      toast.error(userError(requestError, "Tehtävän tilan päivittäminen epäonnistui."));
    } finally {
      setUpdatingTask("");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader icon={CheckSquare} title="Tehtävät" action={<NewTaskDialog onCreated={refreshTasks} />} />

      <EmailTaskSuggestions suggestionId={suggestionId}/>

      {objectives.data?.objectives?.length > 0 && (
        <details className="order-last rounded-2xl border border-border p-3">
          <summary className="min-h-11 cursor-pointer text-sm font-semibold">Viikon tavoitteet</summary>
          <h2 className="font-heading font-bold text-foreground flex items-center gap-2 text-sm"><Target className="h-4 w-4 text-primary" /> Viikon tavoitteet</h2>
          {objectives.data.objectives.map((o) => (
            <div key={o.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`objective-${o.id}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-foreground">{o.title}</p>
                <span className="font-mono text-sm font-semibold text-primary">{o.progress}%</span>
              </div>
              <Progress value={o.progress} className="h-2" />
              <p className="text-[11px] text-muted-foreground mt-2">Lähde: {o.source}</p>
            </div>
          ))}
        </details>
      )}

      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap" data-testid="task-filters">
        {FILTERS.map((f) => (
          <button type="button" key={f.id} data-testid={`filter-${f.id}`} onClick={() => setFilter(f.id)} aria-pressed={filter === f.id}
            className={cn("min-h-11 rounded-2xl px-4 py-2 text-sm font-medium transition-colors",
              filter === f.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground")}>
            {f.label} <span className="ml-1 font-mono">{counts.data?.[f.id]??"—"}</span>
          </button>
        ))}
      </div>
      {counts.error&&<p role="status" className="text-xs text-muted-foreground">Tehtävämääriä ei voitu tarkistaa. Ladatut tehtävät näkyvät alla.</p>}

      <LinkedRecord selection={selection} label="tehtävä" path="/tasks">
        {selection.data && <div className="space-y-2" data-testid="selected-task">
          <h3 className="font-medium">{selection.data.title}</h3>
          <p className="text-sm">{TASK_STATUS[selection.data.status] || selection.data.status} · {selection.data.visibility==="personal" ? "Vain minulle" : "Yrityksen yhteinen"}</p>
          {selection.data.due && <p className="text-sm">Määräaika: {taskDeadline(selection.data.due)}</p>}
          {selection.data.source_email_id&&<a className="text-sm text-primary underline" href={`/inbox?email=${encodeURIComponent(selection.data.source_email_id)}`}>Avaa alkuperäinen sähköposti</a>}
          {selection.data.visibility==="personal"&&selection.data.source_suggestion_id&&<EmailTaskCase task={selection.data}/>}
          <TaskQuickActions key={selection.data.id} task={selection.data} onChanged={refreshTasks} disabled={selection.refreshing || updatingTask===selection.data.id}/>
          <div className="flex flex-wrap gap-2">
            <Select value={selection.data.status} onValueChange={value=>changeStatus(selection.data.id,value)} disabled={selection.refreshing || updatingTask===selection.data.id}>
              <SelectTrigger aria-label="Valitun tehtävän tila" className="w-40"><SelectValue/></SelectTrigger>
              <SelectContent>{Object.entries(TASK_STATUS).map(([key,value])=><SelectItem key={key} value={key}>{value}</SelectItem>)}</SelectContent>
            </Select>
            <EditItemDialog item={selection.data} kind="task" onUpdated={refreshTasks} disabled={selection.refreshing || updatingTask===selection.data.id}/>
          </div>
        </div>}
      </LinkedRecord>
      <ContentSearch value={query} onChange={setQuery} label="Etsi tehtävää" />
      <RefreshNotice refreshing={refreshing} error={data ? error : null} updatedAt={updatedAt} onRetry={refetch} />
      {data&&hasMore&&((counts.data?.[filter]??0)>allLoaded.length||(!counts.data&&allLoaded.length>=200))&&<button type="button" className="order-1 min-h-11 rounded-full border border-border px-4 text-sm disabled:opacity-40" disabled={loadingOlder} onClick={async()=>{const version=generation.current;const target=filter;const base=data;setLoadingOlder(true);try{const page=await getTasks(target,200,allLoaded.length);if(version===generation.current){pageScope.current={data:base,filter:target};setOlder(current=>[...current,...page]);setHasMore(page.some(task=>!allLoaded.some(current=>current.id===task.id)));}}catch(error){if(version===generation.current)toast.error(userError(error,"Lisää tehtäviä ei voitu hakea."));}finally{if(version===generation.current)setLoadingOlder(false);}}}>{loadingOlder?"Haetaan lisää…":"Näytä lisää tehtäviä"}</button>}
      {loading ? <LoadingCards /> : error && !data ? <ErrorState error={error} onRetry={refetch} /> :
        visibleTasks.length === 0 ? <EmptyState icon={CheckSquare} title={query ? "Ei hakutuloksia ladatuissa tehtävissä" : selection.data ? "Ei muita tehtäviä ladatussa listassa" : emptyCopy[0]} hint={query ? "Kokeile toista hakusanaa tai tyhjennä haku." : emptyCopy[1]}
            /> : (
          <div className="space-y-3" data-testid="tasks-list">
            {visibleTasks.map((t) => {
              const timing = dueState(t);
              const priority = PRIORITY[t.priority] || PRIORITY.medium;
              const sourceLink = safeExternalUrl(t.source_link);
              return (
              <div key={t.id} className={cn("rounded-2xl border bg-card p-4 fade-up", timing === "overdue" ? "border-rose-500/40" : timing === "today" ? "border-amber-500/35" : "border-border")} data-testid={`task-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className={cn("font-medium text-foreground", t.status === "done" && "line-through text-muted-foreground")}>{t.title}</p>
                  <span className={cn("shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold", priority.className)}>
                    {priority.label}
                  </span>
                </div>
                {["doing","in_progress"].includes(t.status)&&<p className="mt-2 inline-flex rounded-full bg-sky-500/15 px-3 py-1 text-xs font-semibold text-sky-300">Työn alla</p>}
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {t.assignee}</span>
                  {t.due && <span>Eräpäivä: {taskDeadline(t.due)}</span>}
                  {timing === "overdue" && <span className="font-semibold text-rose-400">Myöhässä</span>}
                  {timing === "today" && <span className="font-semibold text-amber-400">Erääntyy tänään</span>}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Lähde: {t.source}
                  {t.source_email_id&&<a className="ml-2 text-primary underline" href={`/inbox?email=${encodeURIComponent(t.source_email_id)}`}>Avaa sähköposti</a>}
                  {sourceLink && <a href={sourceLink} target="_blank" rel="noopener noreferrer" aria-label={`Avaa tehtävän lähde: ${t.title}`} className="inline-flex items-center gap-0.5 text-primary ml-1"><ExternalLink className="h-3 w-3" /></a>}
                </p>
                {t.suggested_start&&t.suggested_end&&<p className="mt-2 text-xs text-primary">Suositeltu aika: {fiDate(t.suggested_start)} klo {fiTime(t.suggested_start)}–{fiTime(t.suggested_end)} · ei kalenterivarausta</p>}
                <p className="mt-1 text-[11px] text-muted-foreground">{t.visibility === "personal" ? "Vain minulle" : "Yrityksen yhteinen"}</p>
                <TaskQuickActions task={t} onChanged={refreshTasks} disabled={updatingTask===t.id}/>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="w-40">
                  <Select value={t.status} onValueChange={(v) => changeStatus(t.id, v)} disabled={updatingTask === t.id}>
                    <SelectTrigger data-testid={`task-status-${t.id}`} className="h-9 rounded-xl text-xs disabled:opacity-50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TASK_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  </div>
                  <EditItemDialog item={t} kind="task" onUpdated={refreshTasks} disabled={updatingTask === t.id} />
                </div>
              </div>
            )})}
          </div>
        )}
    </div>
  );
}
